import React, { createContext, useContext, useReducer, ReactNode } from 'react';

// Definindo o tipo para o estado
type State = {
    currentStep: number;
    name: string;
    contact: string;  // Adiciona o campo contact para o nome do contato
    level: 0 | 1;
    email: string;
    github: string;
    cnpj: string;
    phone: string;
    ip: string;
    os: 'Link' | 'Linux' | 'Windows';
    host: string;
    groupid: string;  // Inclusão do groupid no estado
    hosts: { name: string; ip: string }[]; // Adiciona a lista de hosts
};

// Definindo o tipo para as ações
type Action = {
    type: FormActions;
    payload: any; // Sugestão: Altere para um tipo mais específico conforme necessário
};

// Enumeração para as ações de formulário
export enum FormActions {
    setCurrentStep,
    setName,
    setContact, // Ação para atualizar o nome do contato
    setLevel,
    setEmail,
    setGithub,
    setCnpj,
    setPhone,
    setIp,
    setOs,
    setHost,
    setGroupId, // Ação para atualizar o groupid
    setHosts // Ação para atualizar a lista de hosts
}

// Definindo o tipo para o contexto
type ContextType = {
    state: State;
    dispatch: React.Dispatch<Action>;
};

// Criando o contexto
const FormContext = createContext<ContextType | undefined>(undefined);

// Estado inicial
const initialData: State = {
    currentStep: 0,
    name: '',
    contact: '', // Valor inicial do nome do contato
    level: 0,
    email: '',
    github: '',
    cnpj: '',
    phone: '',
    ip: '',
    os: 'Link',
    host: '',
    groupid: '',  // Valor inicial de groupid
    hosts: [] // Valor inicial da lista de hosts
};

// Reducer para gerenciar as ações do formulário
const formReducer = (state: State, action: Action): State => {
    switch(action.type) {
        case FormActions.setCurrentStep:
            return { ...state, currentStep: action.payload };
        case FormActions.setName:
            return { ...state, name: action.payload };
        case FormActions.setContact:
            return { ...state, contact: action.payload }; // Atualiza o nome do contato no estado
        case FormActions.setLevel:
            return { ...state, level: action.payload };
        case FormActions.setEmail:
            return { ...state, email: action.payload };
        case FormActions.setGithub:
            return { ...state, github: action.payload };
        case FormActions.setCnpj:
            return { ...state, cnpj: action.payload };
        case FormActions.setPhone:
            return { ...state, phone: action.payload };
        case FormActions.setIp:
            return { ...state, ip: action.payload };
        case FormActions.setOs:
            return { ...state, os: action.payload };
        case FormActions.setHost:
            return { ...state, host: action.payload };
        case FormActions.setGroupId:
            return { ...state, groupid: action.payload }; // Atualiza o groupid no estado
        case FormActions.setHosts:
            return { ...state, hosts: action.payload }; // Atualiza a lista de hosts
        default:
            return state;    
    }
};

// Componente provider para envolver a aplicação ou partes dela
export const FormProvider: React.FC<{children: ReactNode}> = ({ children }) => {
    const [state, dispatch] = useReducer(formReducer, initialData);
    const value = { state, dispatch };

    return (
        <FormContext.Provider value={value}>
            {children}
        </FormContext.Provider>
    );
};

// Hook para usar o contexto do formulário
export const useForm = () => {
    const context = useContext(FormContext);
    if (!context) {
        throw new Error('useForm deve ser usado dentro de um FormProvider');
    }
    return context;
};
